import type { EspacioGondola } from './gondola';

/**
 * La forma de lo que el campo guardó, tal cual está en la base.
 *
 * Vive en `domain/` y no en `api/` porque el jsonb **es el contrato con el formulario**, no con la
 * consulta: los dos formularios de P&G son HTML publicados aparte, fuera de este repo y fuera de
 * cualquier compilador. Si mañana alguien les cambia un campo, lo único que lo puede detectar es
 * un tipo escrito a mano, y tiene que estar donde lo lea quien calcula, no donde lo lea quien
 * consulta.
 *
 * ⚠️ Todo campo de opción es **`string | null`** y no una unión cerrada, a propósito. Una unión
 * cerrada mentiría: el jsonb puede traer cualquier cosa, incluido un valor de una versión vieja
 * del formulario, y TypeScript no valida en tiempo de ejecución. Las listas del catálogo sirven
 * para PINTAR y para ordenar, no para prometer que no va a llegar otra cosa.
 */

/** Una foto: `ruta` en el bucket, o `error` si la subida falló y la ficha se envió igual. */
export interface FotoPg {
  ruta?: string | null;
  nombre?: string | null;
  error?: string | null;
}

// ─────────────────────────────── mercados ───────────────────────────────

export interface PrecioMer {
  cat: string;
  marca: string;
  marca_otra?: string;
  precio: number | null;
  present: string;
  /** Cuántas unidades trae la presentación. `null` cuando no se supo. */
  unidades: number | null;
}

export interface PromoMer {
  dequien?: string;
  marca?: string;
  mecanica?: string;
  texto?: string;
  fotos?: FotoPg[];
}

export interface AbastoMer {
  donde?: string[];
  frecuencia?: string;
  /** El nombre del distribuidor o bróker. NO es «quién se lo lleva». */
  quien?: string;
  /** `''` = no se preguntó · `si` · `no` · `no_dijo`. Los cuatro estados son distintos. */
  visita?: string;
  visita_quien?: string;
}

export interface FichaMerJson {
  version?: number;
  cats?: string[];
  clientes?: string[];
  /** Mapa plano `marca → presencia`. **Clave ausente = no se respondió.** */
  presencia?: Record<string, string>;
  precios?: PrecioMer[];
  promos?: PromoMer[];
  abasto?: AbastoMer;
  recomienda?: { marca?: string; marca_otra?: string; motivo?: string };
  cierre?: { fotos?: FotoPg[]; fuera_de_lista?: string; obs?: string };
  /** `true` = el nombre del mercado se tecleó; `false` = salió de la lista cerrada. */
  mercado_escrito_a_mano?: boolean;
}

export interface FichaMercado {
  id: string;
  supervisor: string | null;
  mercadoNombre: string | null;
  mercadoDireccion: string | null;
  zona: string | null;
  distrito: string | null;
  puestoNumero: number | null;
  puestoNombre: string | null;
  levantado: boolean;
  motivoNo: string | null;
  fecha: string;
  ocurridoEn: string | null;
  registradoEn: string | null;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  ficha: FichaMerJson;
  /** Rutas de Storage ya aplanadas, solo las que subieron bien. */
  fotos: string[];
  flags: string[];
  origin: string | null;
  duracionSeg: number | null;
}

export interface CierreMercado {
  id: string;
  supervisor: string | null;
  mercadoNombre: string | null;
  fecha: string;
  afluencia: string | null;
  actividadAdicional: boolean | null;
  factorExterno: string | null;
  competenciaActiva: string | null;
  observacion: string | null;
}

// ─────────────────────────────── canal moderno ───────────────────────────────

export interface PrecioMod {
  tipo: string;
  producto?: string;
  normal?: string;
  bajo?: string;
  condicion?: string | null;
  /** Solo cuando `tipo === 'choque'`: el producto de enfrente. */
  rival?: string;
  rival_normal?: string;
  rival_bajo?: string;
  motivo?: string | null;
  fotos?: FotoPg[];
}

export interface PromoMod {
  marca?: string;
  mecanica?: string | null;
  tarjeta?: string | null;
  fotos?: FotoPg[];
}

export interface PopMod { tipo?: string; marca?: string; fotos?: FotoPg[] }

export interface ExhibicionMod {
  tipo?: string; ubic?: string; marca?: string; fotos?: FotoPg[];
}

export interface GondolaJson {
  nombre?: string;
  /** `Sí, la revisé toda` · `No pude terminar` · `null`. Decide si los chips sin marcar valen. */
  revisada?: string | null;
  /** Campo muerto: se inicializa y **ninguna pantalla lo escribe nunca**. Siempre `null`. */
  estado?: string | null;
  modulos?: number;
  niveles?: number;
  carro?: string | null;
  items?: Record<string, boolean>;
  espacio?: EspacioGondola;
  precios?: PrecioMod[];
  promos?: PromoMod[];
  pop?: PopMod[];
  fotos?: FotoPg[];
  impulsadora?: string[];
  impulsadora_marca?: string;
  /** Los dos campos libres que son la puerta de entrada de lo que el catálogo no anticipó. */
  otros_pg?: string;
  otros_comp?: string;
  obs?: string;
}

export interface FichaModJson {
  version?: number;
  entrada?: { publicidad_camino?: string | null; publicidad_marca?: string };
  gondolas?: Record<string, GondolaJson>;
  cierre?: {
    completo?: string | null;
    sin_foto?: string | null;
    exhibiciones?: ExhibicionMod[];
    obs?: string;
  };
}

export interface FichaModerno {
  id: string;
  supervisor: string | null;
  pdvId: string | null;
  pdvNombre: string | null;
  tiendaTexto: string | null;
  /** `null` **si y solo si** la tienda se tecleó a mano. Nunca está sucia. */
  cadena: string | null;
  fecha: string;
  ocurridoEn: string | null;
  registradoEn: string | null;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  ficha: FichaModJson;
  fotos: string[];
  flags: string[];
  origin: string | null;
}

/**
 * 🔴 **Fuera de la maestra NO es `pdv_id is null`.**
 *
 * De 24 fichas, 19 tienen `pdv_id` en NULL pero solo **3** se tecleraon a mano. Las otras 16 se
 * eligieron de la lista del formulario —traen `cadena`— y simplemente no se guardó el id. Usar
 * `pdv_id` como criterio le diría al jefe de operaciones que el maestro `pdv` se quedó corto en
 * dos tercios del canal moderno, y lo mandaría a cargar un catálogo que ya existe.
 *
 * El criterio real es la bandera que pone el formulario con `if(!S.tienda)`, y `cadena is null`
 * coincide exacto con ella. Se usa la bandera porque es la intención declarada; `cadena` es la
 * consecuencia.
 *
 * Una sola definición, usada por la señal de la fila **y** por el indicador del resumen: dos
 * caminos para la misma pregunta es cómo las dos solapas terminan contradiciéndose.
 */
export const fueraDeLaMaestra = (f: FichaModerno): boolean =>
  f.flags.includes('tienda_fuera_de_maestra');

/** Cómo se llama la tienda: el nombre del maestro si lo hay, y si no lo que se escribió. */
export const nombreDeTienda = (f: FichaModerno): string =>
  f.pdvNombre ?? f.tiendaTexto ?? '—';
